<script setup lang="ts">
  import { computed } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';
  import IconImage from '@/shared/ui/common/IconImage.vue';
  import ColoredButton from '@/shared/ui/buttons/ColoredButton.vue';

  const props = withDefaults(
    defineProps<{
      // SVGs are imported as components in this project, so an icon is a
      // component object as often as it is a path. IconImage takes both.
      icon?: string | object;
      iconAlt?: string;
      title: string;
      text: string;
      /** Rendered in bold where `text` holds the `{highlight}` token. */
      highlight?: string;
      actionButtonText: string;
      /** Without it the modal only informs: the action button is the one way out. */
      cancelButtonText?: string;
      clickToClose?: boolean;
      escToClose?: boolean;
      isSubmitting?: boolean;
    }>(),
    {
      cancelButtonText: undefined,
      clickToClose: true,
      escToClose: true,
      isSubmitting: false,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'confirm'): void;
  }>();

  /**
   * While a submission is in flight the modal is sealed: neither button, the X,
   * the overlay nor ESC may dismiss it, and confirming again must not fire a
   * second request. Guarding the emits rather than only disabling the buttons
   * keeps the rule where it cannot be bypassed.
   */
  const canDismiss = computed(() => !props.isSubmitting);

  const HIGHLIGHT_TOKEN = '{highlight}';

  const highlightedText = computed(() => {
    const index = props.text.indexOf(HIGHLIGHT_TOKEN);
    if (props.highlight === undefined || index === -1) return null;

    return {
      before: props.text.slice(0, index),
      after: props.text.slice(index + HIGHLIGHT_TOKEN.length),
    };
  });

  function requestClose(): void {
    if (props.isSubmitting) return;
    emit('close');
  }

  function requestConfirm(): void {
    if (props.isSubmitting) return;
    emit('confirm');
  }
</script>

<template>
  <VueFinalModal
    class="flex justify-center items-center"
    content-class="py-4 px-4 flex flex-col bg-white-01 rounded-2xl w-[500px] shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    :click-to-close="props.clickToClose && canDismiss"
    :esc-to-close="props.escToClose && canDismiss"
  >
    <div class="overflow-auto">
      <div class="flex justify-end">
        <button
          class="text-gray-03 disabled:opacity-50"
          :disabled="props.isSubmitting"
          aria-label="Fechar"
          @click="requestClose"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <div>
        <div
          v-if="icon"
          class="flex justify-center items-center"
        >
          <IconImage
            v-if="icon && iconAlt"
            :src="icon"
            :alt="iconAlt || ''"
          />
        </div>
        <div class="py-4">
          <h3 class="text-xl font-medium text-center mb-2">
            {{ title }}
          </h3>
          <p class="text-gray-03 text-center mb-6">
            <template v-if="highlightedText">
              {{ highlightedText.before }}<strong class="font-bold">{{ highlight }}</strong>{{ highlightedText.after }}
            </template>
            <template v-else>
              {{ text }}
            </template>
          </p>
        </div>

        <div class="pb-6 flex justify-center gap-4">
          <ColoredButton
            v-if="cancelButtonText"
            outlined
            color="gray-03"
            :text="cancelButtonText"
            :disabled="props.isSubmitting"
            @click="requestClose"
          />
          <ColoredButton
            :text="actionButtonText"
            :disabled="props.isSubmitting"
            :loading="props.isSubmitting"
            @click="requestConfirm"
          />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<style scoped>
  .vfm__overlay {
    background-color: rgba(0, 0, 0, 0.5);
  }
</style>
